import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { Download, Mail, MessageCircle, Pencil, Plus } from 'lucide-react';
import { db } from '../../../firebase';
import BidModal from './BidModal';
import { downloadBidPdf, getBidEmailBody, getBidSmsBody } from './bidPdf';

const Estimator = () => {
  const [bids, setBids] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBid, setEditingBid] = useState(null);

  useEffect(() => {
    const unsubscribers = [];
    const q = query(collection(db, 'bids'), orderBy('updatedAt', 'desc'));
    const bidsUnsub = onSnapshot(q, (snapshot) => {
      setBids(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubscribers.push(bidsUnsub);

    const custUnsub = onSnapshot(collection(db, 'customers'), (snapshot) => {
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubscribers.push(custUnsub);

    return () => unsubscribers.forEach(u => u());
  }, []);

  const openNew = () => {
    setEditingBid(null);
    setShowModal(true);
  };

  const openEdit = (bid) => {
    setEditingBid(bid);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBid(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bid?')) return;
    try {
      await deleteDoc(doc(db, 'bids', id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete bid.');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const t = d?.toDate ? d.toDate() : new Date(d);
    return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

  const statusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  };

  const handleEmailBid = (bid) => {
    const subject = encodeURIComponent(`Bid from Driscollland – ${bid.customerName || 'Estimate'}`);
    const body = encodeURIComponent(getBidEmailBody(bid));
    const addr = bid.email ? `${bid.email}?` : '';
    window.location.href = `mailto:${addr}subject=${subject}&body=${body}`;
  };

  const handleSmsBid = (bid) => {
    const body = encodeURIComponent(getBidSmsBody(bid));
    const num = bid.phone ? bid.phone.replace(/\D/g, '') : '';
    if (num) {
      window.location.href = `sms:${num}?body=${body}`;
    } else {
      window.location.href = `sms:?body=${body}`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-secondary-900">Estimator</h2>
          <p className="text-secondary-600 text-sm">Create and manage bids for all services.</p>
        </div>
        <button type="button" onClick={openNew} className="btn-primary whitespace-nowrap inline-flex items-center gap-2">
          <Plus className="w-5 h-5" />
          New bid
        </button>
      </div>

      {showModal && (
        <BidModal
          onClose={closeModal}
          customers={customers}
          editingBid={editingBid}
        />
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Customer</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Contact</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-secondary-600 uppercase">Total</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Status</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-secondary-600 uppercase">Updated</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-secondary-600 uppercase">Send / Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {bids.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-secondary-500">
                    No bids yet. Create a bid to get started.
                  </td>
                </tr>
              ) : (
                bids.map((bid) => (
                  <tr key={bid.id} className="hover:bg-secondary-50/50">
                    <td className="px-4 py-2.5 text-sm font-medium text-secondary-900">{bid.customerName || '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-secondary-600">
                      {[bid.email, bid.phone].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium text-secondary-900">{formatCurrency(bid.total)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusColor(bid.status)}`}>
                        {bid.status || 'draft'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right text-secondary-600">{formatDate(bid.updatedAt || bid.createdAt)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                        <button type="button" onClick={() => downloadBidPdf(bid)} className="inline-flex items-center gap-1 text-secondary-700 hover:text-primary-600 text-sm font-medium" title="Download PDF">
                          <Download className="w-4 h-4" /> PDF
                        </button>
                        <button type="button" onClick={() => handleEmailBid(bid)} className="inline-flex items-center gap-1 text-secondary-700 hover:text-primary-600 text-sm font-medium" title="Open in email">
                          <Mail className="w-4 h-4" /> Email
                        </button>
                        <button type="button" onClick={() => handleSmsBid(bid)} className="inline-flex items-center gap-1 text-secondary-700 hover:text-primary-600 text-sm font-medium" title="Open in SMS">
                          <MessageCircle className="w-4 h-4" /> SMS
                        </button>
                        <span className="text-secondary-200">|</span>
                        <button type="button" onClick={() => openEdit(bid)} className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium">
                          <Pencil className="w-4 h-4" /> Edit
                        </button>
                        <button type="button" onClick={() => handleDelete(bid.id)} className="text-red-600 hover:text-red-700 text-sm font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Estimator;
